import React, { useEffect, useState } from 'react';
import { deleteSession, getSessions } from '../../services/api';

const formatTimestamp = (timestamp) => new Date(timestamp * 1000).toLocaleString().replace(',', '');

const pluralize = (value, singular, plural) => (value === 1 ? singular : plural);

const formatRelativeTimestamp = (timestamp) => {
  const elapsedSeconds = Math.max(0, Math.floor(Date.now() / 1000) - timestamp);
  if (elapsedSeconds < 60) return 'Just now';

  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) return `${elapsedMinutes} ${pluralize(elapsedMinutes, 'min', 'mins')} ago`;

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  const remainingMinutes = elapsedMinutes % 60;
  if (elapsedHours < 24) {
    const minutes = remainingMinutes
      ? ` ${remainingMinutes} ${pluralize(remainingMinutes, 'minute', 'minutes')}`
      : '';
    return `${elapsedHours} ${pluralize(elapsedHours, 'hour', 'hours')}${minutes} ago`;
  }

  const elapsedDays = Math.floor(elapsedHours / 24);
  const remainingHours = elapsedHours % 24;
  const hours = remainingHours ? ` ${remainingHours} ${pluralize(remainingHours, 'hour', 'hours')}` : '';
  const minutes = remainingMinutes
    ? ` ${remainingMinutes} ${pluralize(remainingMinutes, 'minute', 'minutes')}`
    : '';
  return `${elapsedDays} ${pluralize(elapsedDays, 'day', 'days')}${hours}${minutes} ago`;
};

const TimestampCell = ({ timestamp }) => (
  <>
    <div>{formatRelativeTimestamp(timestamp)}</div>
    <div className="session-secondary-text">{formatTimestamp(timestamp)}</div>
  </>
);

function SessionsList({ onCurrentSessionEnded }) {
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [endingSessionId, setEndingSessionId] = useState(null);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getSessions();
      setSessions(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const handleEndSession = async (session) => {
    const message = session.isCurrent
      ? 'End the current session? You will be signed out.'
      : 'End this session? The device will need to sign in again.';

    if (!window.confirm(message)) return;

    try {
      setEndingSessionId(session.sessionId);
      setError('');
      const response = await deleteSession(session.sessionId);

      if (response.data.currentSessionEnded) {
        onCurrentSessionEnded();
        return;
      }

      setSessions((currentSessions) => currentSessions.filter((item) => item.sessionId !== session.sessionId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to end session');
    } finally {
      setEndingSessionId(null);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Your sessions</h2>
        <button type="button" className="btn btn-secondary" onClick={loadSessions} disabled={loading}>
          Refresh
        </button>
      </div>

      {error && <div className="error" style={{ marginBottom: '15px' }}>{error}</div>}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Start</th>
              <th>Last active</th>
              <th>Last IP</th>
              <th>User-Agent</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5">Loading sessions...</td></tr>
            ) : sessions.length === 0 ? (
              <tr><td colSpan="5">No active sessions</td></tr>
            ) : sessions.map((session) => (
              <tr key={session.sessionId} className={session.isCurrent ? 'current-session-row' : ''}>
                <td><TimestampCell timestamp={session.startedAt} /></td>
                <td><TimestampCell timestamp={session.lastActiveAt} /></td>
                <td>{session.lastIp}</td>
                <td className="session-user-agent">{session.lastUserAgent}</td>
                <td>
                  {!session.isCurrent && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => handleEndSession(session)}
                      disabled={endingSessionId === session.sessionId}
                    >
                      {endingSessionId === session.sessionId ? 'Ending...' : 'End session'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SessionsList;
